import { TokenHelper } from '@deltax/idp-api-client'

import errorCodes from '@/constants/errorCodes'
import AppService from '@/services/appService'
import ForbiddenBusinessProfileRequestedError from '@/errors/ForbiddenBusinessProfileRequestedError'
import InvalidBusinessProfileRequestedError from '@/errors/InvalidBusinessProfileRequestedError'
import InvalidRequestDataException from '@/errors/InvalidRequestDataException'
import RefreshTokenExpiredException from '@/errors/RefreshTokenExpiredException'
import TokenExpiredException from '@/errors/TokenExpiredException'
import TokenInvalidException from '@/errors/TokenInvalidException'
import TokenMalformedException from '@/errors/TokenMalformedError'
import UnauthorizedError from '@/errors/UnauthorizedError'
import UserAccountExpiredException from '@/errors/UserAccountExpiredException'

import { authService, apiClient, isEmbedded } from './client'

type NotifyFn = (message: string, color: string, showLoader?: boolean, persistSnackbar?: boolean) => void

let notifyFn: NotifyFn | null = null

export function setApiErrorNotifier(notifier: NotifyFn): void {
  notifyFn = notifier
}

function clearSessionAndRedirectToLogin(): void {
  const currentPath = window.location.pathname + window.location.search + window.location.hash
  const redirectTarget =
    !currentPath.startsWith('/login') && !currentPath.startsWith('/callback') ? currentPath : null
  if (redirectTarget) {
    sessionStorage.setItem('dx_internal_redirect', redirectTarget)
  }
  TokenHelper.removeTokens(apiClient.storage, apiClient.accessTokenStorageKey)
  authService.setAccessToken('')
  localStorage.removeItem('accessToken')
  window.onbeforeunload = null
  window.location.href = redirectTarget ? `/login?redirect=${encodeURIComponent(redirectTarget)}` : '/login'
}

function notifyError(error: unknown): void {
  notifyFn?.('Something went wrong', 'error')
}

function getErrorMessage(data?: { message?: string }): string {
  return data?.message ?? 'An unexpected error occurred'
}

export async function handleError(error: unknown): Promise<never> {
  // In embed mode, authentication recovery is handled via session token re-bootstrapping
  // (client.ts errorHandler). Never redirect the iframe to /login or clear tokens here.
  if (isEmbedded) {
    const { isTokenVersionRevoked, handleEmbedFlowError } = await import('@/helpers/embedHelper')
    if (isTokenVersionRevoked(error)) {
      handleEmbedFlowError(error)
      return Promise.reject(error)
    }

    if ((error as Error)?.name === 'AuthRefreshError' || (error as { isAuthRefreshError?: boolean })?.isAuthRefreshError) {
      const errObj = error as Error
      if (errObj?.message?.includes('Retried request failed with 401')) {
        if (window.location.pathname !== '/forbidden') {
          window.location.href = '/forbidden'
        }
      }
      return Promise.reject(error)
    }

    const errObj = error as {
      response?: { data?: { errorCode?: number; message?: string }; status?: number }
    }
    const status = errObj?.response?.status
    const errorCode = errObj?.response?.data?.errorCode
    if (
      status === errorCodes.Unauthorized ||
      errorCode === errorCodes.Unauthorized ||
      errorCode === errorCodes.TokenExpiredException ||
      errorCode === errorCodes.RefreshTokenExpired ||
      errorCode === errorCodes.TokenInvalidException ||
      errorCode === errorCodes.TokenMalformedException
    ) {
      return Promise.reject(error)
    }
  }

  if ((error as Error)?.name === 'AuthRefreshError' || (error as { isAuthRefreshError?: boolean })?.isAuthRefreshError) {
    const errObj = error as Error
    if (errObj?.message?.includes('Retried request failed with 401')) {
      if (window.location.pathname !== '/forbidden') {
        window.location.href = '/forbidden'
      }
      return Promise.reject(error)
    }
    const appService = new AppService()
    await appService.recoverSession()
    return Promise.reject(error)
  }

  const err = error as {
    response?: { data?: { errorCode?: number; message?: string }; status?: number }
  }

  if (err?.response && (err.response.data || err.response.status === errorCodes.Unauthorized)) {
    const errorCode: number =
      err.response.data?.errorCode === undefined
        ? (err.response.status as number)
        : err.response.data.errorCode
    const message = getErrorMessage(err.response.data)

    switch (errorCode) {
      case errorCodes.UserAccountExpiredException:
        return Promise.reject(new UserAccountExpiredException(message))
      case errorCodes.InvalidBusinessProfileRequested:
        return Promise.reject(new InvalidBusinessProfileRequestedError(message))
      case errorCodes.InvalidRequestDataException:
        return Promise.reject(new InvalidRequestDataException(message))
      case errorCodes.ForbiddenBusinessProfileRequested:
        window.location.href = '/not-found'
        return Promise.reject(new ForbiddenBusinessProfileRequestedError(message))
      case errorCodes.TokenExpiredException: {
        const appService = new AppService()
        await appService.recoverSession()
        return Promise.reject(new TokenExpiredException(message))
      }
      case 400:
      case errorCodes.TokenMalformedException:
      case errorCodes.TokenInvalidException:
      case errorCodes.ForbiddenPortalRequested:
      case errorCodes.Forbidden:
      case errorCodes.Unauthorized:
        if (window.location.pathname !== '/forbidden') {
          window.location.href = '/forbidden'
        }
        return Promise.reject(new UnauthorizedError(message))
      case errorCodes.RefreshTokenExpired: {
        const appService = new AppService()
        await appService.recoverSession()
        return Promise.reject(new RefreshTokenExpiredException(message))
      }
      default:
        notifyError(error)
        return Promise.reject(error)
    }
  }

  if (error) {
    notifyError(error)
  }
  return Promise.reject(error)
}
